import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardActions, Typography, TextField, Button, MenuItem, Alert, CircularProgress, Stack, RadioGroup, FormControlLabel, Radio, Switch, FormGroup, Stepper, Step, StepLabel } from '@mui/material';
import axios from 'axios';

const CourseGenerator = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [paramsText, setParamsText] = useState('{}');
  const [paramsObj, setParamsObj] = useState({});
  const [courseMetaText, setCourseMetaText] = useState('{}');
  const [saveAs, setSaveAs] = useState('draft');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    axios
      .get('/api/admin/prompt_templates/?_start=0&_end=100&_sort=created_at&_order=DESC', {
        headers: {
          ...(userId ? { 'X-User-Id': userId } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
      .then((res) => {
        setTemplates(res.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load prompt templates', err);
        setError('Failed to load prompt templates');
        setLoading(false);
      });
  }, []);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // Initialize empty params when template changes (params_schema removed)
  useEffect(() => {
    if (!selectedTemplate) return;
    setParamsObj({});
    setParamsText('{}');
  }, [selectedTemplate]);

  const parseJson = (text) => {
    try {
      if (!text || !text.trim()) return {};
      return JSON.parse(text);
    } catch (e) {
      throw new Error('Invalid JSON');
    }
  };

  // Keep paramsObj in sync when user edits the raw JSON textarea
  useEffect(() => {
    try {
      const obj = parseJson(paramsText);
      setParamsObj(obj);
    } catch {
      // leave paramsObj as-is until JSON is valid
    }
  }, [paramsText]);

  // JSON validation helper
  const isValidJson = (text) => {
    try {
      parseJson(text);
      return true;
    } catch {
      return false;
    }
  };

  // No schema validation (params_schema removed from database)
  const requiredSet = useMemo(() => new Set(), []);

  // Helpers for schema-driven form
  function getInitialValuesFromSchema(schema) {
    const result = {};
    if (schema && schema.type === 'object' && schema.properties) {
      Object.entries(schema.properties).forEach(([key, prop]) => {
        if (prop && Object.prototype.hasOwnProperty.call(prop, 'default')) {
          result[key] = prop.default;
        } else if (prop.type === 'string') {
          result[key] = '';
        } else if (prop.type === 'number' || prop.type === 'integer') {
          result[key] = 0;
        } else if (prop.type === 'boolean') {
          result[key] = false;
        } else if (prop.type === 'array') {
          result[key] = [];
        } else if (prop.type === 'object') {
          result[key] = {};
        } else {
          result[key] = '';
        }
      });
    }
    return result;
  }

  function renderParamField(name, prop) {
    const baseLabel = prop?.title || name;
    const label = requiredSet.has(name) ? `${baseLabel} *` : baseLabel;
    const value = paramsObj?.[name];

    const update = (nextVal) => {
      const next = { ...paramsObj, [name]: nextVal };
      setParamsObj(next);
      setParamsText(JSON.stringify(next, null, 2));
    };

    if (prop && Array.isArray(prop.enum)) {
      return (
        <TextField key={name} select label={label} value={value ?? ''} onChange={(e) => update(e.target.value)} fullWidth helperText={prop?.description || ''}>
          {prop.enum.map((opt) => (
            <MenuItem key={String(opt)} value={opt}>{String(opt)}</MenuItem>
          ))}
        </TextField>
      );
    }

    switch (prop?.type) {
      case 'string':
        return (
          <TextField key={name} label={label} value={value ?? ''} onChange={(e) => update(e.target.value)} fullWidth helperText={prop?.description || ''} />
        );
      case 'number':
      case 'integer':
        return (
          <TextField key={name} type="number" label={label} value={value ?? ''} onChange={(e) => update(e.target.value === '' ? '' : Number(e.target.value))} fullWidth helperText={prop?.description || ''} />
        );
      case 'boolean':
        return (
          <FormGroup key={name}>
            <FormControlLabel control={<Switch checked={!!value} onChange={(e) => update(e.target.checked)} />} label={label} />
          </FormGroup>
        );
      default:
        return (
          <Alert key={name} severity="info">Unsupported field type for "{name}". Use the JSON editor below.</Alert>
        );
    }
  }

  const handleGenerate = async () => {
    setError(null);
    setResult(null);

    if (!selectedTemplateId) {
      setError('Please select a prompt template.');
      return;
    }
    let paramsObj, metaObj;
    try {
      paramsObj = parseJson(paramsText);
      metaObj = parseJson(courseMetaText);
    } catch (e) {
      setError('Params or Course Meta is not valid JSON.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        prompt_template_id: selectedTemplateId,
        params: paramsObj,
        course_meta: metaObj,
        save_as: saveAs,
      };
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/courses/generate_from_template', payload, {
        headers: {
          ...(userId ? { 'X-User-Id': userId } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setResult(res.data);
      setActiveStep(3);
    } catch (e) {
      console.error('Generation failed', e);
      const msg = e?.response?.data?.detail || e.message || 'Generation failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Wizard navigation & validation
  const steps = ['Select Template', 'Fill Parameters', 'Course Meta', 'Review & Generate'];

  const canProceedFromStep = (step) => {
    if (step === 0) return !!selectedTemplateId;
    if (step === 1) {
      // Require valid JSON only (no schema validation)
      try {
        parseJson(paramsText);
        return true;
      } catch {
        return false;
      }
    }
    if (step === 2) {
      // Course meta is optional but must be valid JSON if provided
      try { parseJson(courseMetaText); return true; } catch { return false; }
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceedFromStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  if (loading) return <CircularProgress/>;
  if (error && !result) return <Alert severity="error">{error}</Alert>;

  return (
    <Card sx={{ m: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Course Generator
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <TextField
              select
              label="Prompt Template"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              helperText="Choose a prompt template"
              fullWidth
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {activeStep === 1 && (
            <>
              <TextField
                label="Parameters (JSON)"
                multiline
                rows={6}
                fullWidth
                value={paramsText}
                onChange={(e) => setParamsText(e.target.value)}
                helperText="Enter parameters as JSON object, e.g., {&quot;title&quot;: &quot;My Course&quot;, &quot;complexity&quot;: &quot;beginner&quot;}"
                error={paramsText && !isValidJson(paramsText)}
              />
            </>
          )}

          {activeStep === 2 && (
            <TextField
              label="Course Meta (JSON)"
              value={courseMetaText}
              onChange={(e) => setCourseMetaText(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              helperText="Optional metadata like title/description/category/complexity, available to the template as course_meta"
            />
          )}

          {activeStep === 3 && (
            <>
              <RadioGroup row value={saveAs} onChange={(e) => setSaveAs(e.target.value)}>
                <FormControlLabel value="draft" control={<Radio />} label="Save as Draft" />
                <FormControlLabel value="final" control={<Radio />} label="Save as Final" />
              </RadioGroup>
              {result && (
                <Alert severity="success">Generated successfully. CourseMaterial status: {result.status}</Alert>
              )}
              {result?.course_material?.material && (
                <TextField
                  label="Generated Material (readonly)"
                  value={JSON.stringify(result.course_material.material, null, 2)}
                  multiline
                  minRows={14}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              )}
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </CardContent>
      <CardActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
        {activeStep < steps.length - 1 && (
          <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep(activeStep)}>
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button variant="contained" onClick={handleGenerate} disabled={submitting || !selectedTemplateId}>
            {submitting ? 'Generating…' : 'Generate'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default CourseGenerator;
